import { EmployeeDto } from '@zorro/clients';

/**
 * How long a generated document's title may run before the name gives way.
 * Titles are read in a file list, where a long one is truncated by whatever is
 * showing it — so the shortening is ours to choose rather than the OS's.
 */
export const TITLE_MAX_LENGTH = 40;

/**
 * Names a generated document, shortening the employee's name until the title
 * fits: everything, then without the middle name, then the first name alone,
 * then initials. A surname identifies a person better than a middle name, so
 * it survives a rung longer.
 *
 * The suffix is never dropped, since it is what says which document this is.
 * A long enough suffix can still push the title past the budget — the ladder
 * shortens what it can rather than promising a length.
 */
export const buildTitleWithFittedName = (
  employee: Pick<EmployeeDto, 'firstName' | 'lastName'>,
  suffix: string
): string => {
  const givenNames = employee.firstName?.trim() ?? '';
  const lastName = employee.lastName?.trim() ?? '';
  const firstName = givenNames.split(/\s+/u)[0]; // "Alexandria Michelle" -> "Alexandria"
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

  const names = [
    `${givenNames} ${lastName}`.trim(),
    `${firstName} ${lastName}`.trim(), // drop middle names
    firstName, // drop last name
    initials,
  ];
  const fitting = names.find(
    (name) => `${name} ${suffix}`.trim().length <= TITLE_MAX_LENGTH
  );

  return `${fitting ?? initials} ${suffix}`.trim();
};
